<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ChevronsLeft, ChevronsRight } from 'lucide-vue-next'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useSidebarStore } from '@/modules/shared/stores/sidebar-store'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { VIcon, VLink, VAvatar } from '@/modules/shared/components'
import { useSidenav } from '@/modules/shared/composables/use-sidenav'
import { rails_url } from '@/modules/shared/utils/rails'

const route = useRoute()
const authStore = useAuthStore()
const sidebarStore = useSidebarStore()
const workspaceStore = useWorkspaceStore()

const {
  titleCase,
  current_user,
  firms,
  isFirmActive,
  selectFirm,
  profile_links,
  navItems,
  isNavActive,
  isSubItemActive,
  isGroupActive,
} = useSidenav()

const stopViewingAsUrl = computed(
  () => `${rails_url()}/login-as/${authStore.current_user.original_user_id}?new_dashboard=true`,
)

// Desktop rail — profile dropdown.
const profileDropdownTarget = ref(null)
const isProfileClicked = ref(false)
onClickOutside(profileDropdownTarget, () => (isProfileClicked.value = false))

// Mobile drawer — its own profile dropdown.
const mobileProfileTarget = ref(null)
const isMobileProfileClicked = ref(false)
onClickOutside(mobileProfileTarget, () => (isMobileProfileClicked.value = false))

const isBannerVisible = computed(
  () => workspaceStore.current.options.is_setup_mode_enabled && authStore.is_site_or_group_admin,
)

// The mobile top bar drops below the setup banner when it is showing.
const topBarOffsetClass = computed(() => (isBannerVisible.value ? 'top-9' : 'top-1'))

// Close the mobile drawer on navigation.
watch(
  () => route.fullPath,
  () => sidebarStore.closeMobile(),
)

// Lock body scroll while the mobile drawer is open.
watch(
  () => sidebarStore.isMobileOpen,
  (open) => {
    document.body.style.overflow = open ? 'hidden' : ''
  },
)
onBeforeUnmount(() => {
  document.body.style.overflow = ''
})
</script>

<template>
  <!-- Desktop rail (collapsible) -->
  <aside
    class="relative z-20 hidden h-full flex-none border-r border-gray-200 bg-white transition-[width] duration-200 sm:flex sm:flex-col"
    :class="sidebarStore.widthClass"
  >
    <!-- Logo + Firm switcher -->
    <div class="flex flex-none flex-col px-5 pb-2 pt-4">
      <RouterLink :to="{ name: 'dashboard' }" class="flex-shrink-0">
        <img
          :src="workspaceStore.current.logo_url || '/v-logo-192x192.png'"
          class="object-contain hover:opacity-90"
          :class="[
            workspaceStore.current.logo_url ? 'max-h-8 max-w-[160px]' : 'w-6',
            !sidebarStore.isExpanded && workspaceStore.current.logo_url ? 'max-w-[32px]' : '',
          ]"
        />
      </RouterLink>
      <div v-if="sidebarStore.isExpanded && firms.length > 1" class="mt-4 border-y border-gray-200 py-3">
        <div class="mb-1 px-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">Firm</div>
        <ul class="space-y-0.5">
          <li v-for="firm in firms" :key="firm.subdomain">
            <button
              type="button"
              class="flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left text-sm leading-4"
              :class="
                isFirmActive(firm)
                  ? 'bg-brand/10 font-medium text-brand'
                  : 'text-gray-700 hover:bg-gray-900/5 active:bg-gray-200'
              "
              @click="selectFirm(firm)"
            >
              <span class="truncate">{{ firm.name }}</span>
              <span v-if="isFirmActive(firm)" class="h-2 w-2 flex-shrink-0 rounded-full bg-brand" />
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Viewing-as banner (desktop) -->
    <a
      v-if="authStore.logged_in_as"
      :href="stopViewingAsUrl"
      class="group relative mx-3 mt-3 block rounded-md bg-[#ff853a] text-white hover:bg-[#e5762f]"
      :class="sidebarStore.isExpanded ? 'px-3 py-2' : 'flex items-center justify-center p-1.5'"
    >
      <template v-if="sidebarStore.isExpanded">
        <div class="truncate text-xs font-medium">Viewing as {{ current_user?.name }} · read-only</div>
        <div class="mt-1 inline-block rounded bg-white/30 px-2 py-0.5 text-xs font-medium hover:bg-white/50">
          Stop viewing as
        </div>
      </template>
      <template v-else>
        <VIcon name="EyeOff" class="h-4 w-4" />
        <span
          class="pointer-events-none absolute left-full ml-3 hidden whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm group-hover:block"
        >
          Stop viewing as {{ current_user?.name }}
        </span>
      </template>
    </a>

    <!-- Nav items -->
    <nav class="flex-1 overflow-y-auto px-3 pt-5">
      <ul>
        <li v-for="(item, index) in navItems" :key="item?.key ?? `divider-${index}`">
          <div v-if="!item" class="my-2 flex items-center justify-center">
            <div v-if="sidebarStore.isExpanded" class="w-full border-t border-gray-200" />
            <div v-else class="h-1 w-1 rounded-full bg-gray-300" />
          </div>

          <!-- Group (e.g. Settings, Accounts): header + sub-items. When `collapsible`,
               sub-items show only while the group is active; otherwise always visible. -->
          <template v-else-if="item.subItems">
            <VLink
              v-show="!item.hidden"
              :to="item.to"
              class="flex cursor-pointer items-center gap-4 whitespace-nowrap rounded-md px-1.5 py-0.5 text-sm font-medium leading-4"
              :class="
                isGroupActive(item)
                  ? 'text-[#3b88af] hover:bg-[#3b88af]/10 active:bg-[#3b88af]/20'
                  : 'text-gray-700 hover:bg-gray-900/5 active:bg-gray-200'
              "
            >
              <span class="inline-flex flex-shrink-0 items-center justify-center rounded-md bg-transparent p-1.5">
                <component :is="item.icon" class="h-4 w-4" />
              </span>
              <span>{{ item.label }}</span>
            </VLink>
            <ul
              v-show="!item.hidden && (!item.collapsible || isGroupActive(item))"
              class="mb-1 mt-0.5 space-y-0.5 pl-9"
            >
              <li v-for="subItem in item.subItems" :key="subItem.key" v-show="!subItem.hidden">
                <VLink
                  :to="subItem.to"
                  class="flex cursor-pointer items-center rounded-md px-3 py-1 text-xs leading-4"
                  :class="
                    isSubItemActive(subItem)
                      ? 'bg-[#3b88af]/10 font-medium text-[#3b88af]'
                      : 'text-gray-600 hover:bg-gray-900/5 active:bg-gray-200'
                  "
                >
                  {{ subItem.label }}
                </VLink>
              </li>
            </ul>
          </template>

          <component
            v-else
            v-show="!item.hidden"
            :is="typeof item.to === 'string' ? 'a' : 'RouterLink'"
            v-bind="typeof item.to === 'string' ? { href: item.to } : { to: item.to }"
            class="group relative flex cursor-pointer items-center gap-4 whitespace-nowrap rounded-md px-1.5 py-0.5 text-sm font-medium leading-4"
            :class="[
              isNavActive(item)
                ? 'text-[#3b88af] hover:bg-[#3b88af]/10 active:bg-[#3b88af]/20'
                : 'text-gray-700 hover:bg-gray-900/5 active:bg-gray-200',
            ]"
          >
            <span class="inline-flex flex-shrink-0 items-center justify-center rounded-md bg-transparent p-1.5">
              <component :is="item.icon" class="h-4 w-4" />
            </span>
            <span
              class="overflow-hidden whitespace-nowrap transition-[width,opacity] duration-200"
              :class="sidebarStore.isExpanded ? 'w-auto opacity-100' : 'w-0 opacity-0'"
            >
              {{ item.label }}
            </span>
            <span
              v-if="item.badge && sidebarStore.isExpanded"
              class="rotate-12 rounded bg-[#3b88af] px-1 py-0.5 text-[9px] font-semibold leading-none text-white"
            >
              {{ item.badge }}
            </span>
            <span
              v-if="!sidebarStore.isExpanded"
              class="pointer-events-none absolute left-full ml-3 hidden whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm group-hover:block"
            >
              {{ item.label }}
            </span>
          </component>
        </li>
      </ul>
    </nav>

    <!-- Bottom section: profile -->
    <div class="flex-none border-t border-gray-200 px-3 py-3">
      <div class="relative" ref="profileDropdownTarget">
        <div
          class="flex cursor-pointer items-center gap-3 rounded-md px-1.5 py-1.5 hover:bg-gray-900/5"
          @click="isProfileClicked = !isProfileClicked"
        >
          <VAvatar :image="current_user?.profile_picture" :initials="current_user?.initials" size="sm" />
          <div v-if="sidebarStore.isExpanded" class="min-w-0 flex-1 overflow-hidden">
            <div class="truncate text-sm font-semibold leading-tight">{{ titleCase(current_user?.name) }}</div>
            <div class="truncate text-xs text-gray-500">{{ titleCase(current_user?.role?.replace('_', ' ')) }}</div>
          </div>
          <span
            v-if="!sidebarStore.isExpanded"
            class="pointer-events-none absolute left-full ml-3 hidden whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm group-hover:block"
          >
            {{ titleCase(current_user?.name) }}
          </span>
        </div>
        <!-- Profile dropdown -->
        <div
          v-if="isProfileClicked"
          class="absolute bottom-full left-0 z-30 mb-1 flex min-w-[200px] flex-col gap-0.5 rounded-lg border border-gray-300 bg-white py-1 shadow-lg"
        >
          <VLink
            :to="link.to"
            v-for="link in profile_links"
            :key="link.label"
            class="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-[#e8f1f7] hover:text-[#006591]"
          >
            <component :is="link.icon" class="h-4 w-4 text-gray-500" />
            {{ link.label }}
          </VLink>
        </div>
      </div>
    </div>
  </aside>

  <!-- Mobile top bar -->
  <div
    class="fixed left-0 right-0 z-30 flex h-[50px] items-center gap-3 bg-white px-3 shadow-sm sm:hidden"
    :class="topBarOffsetClass"
  >
    <VIcon name="BurgerMenu" class="block h-5 w-5 cursor-pointer" @click="sidebarStore.toggleMobile" />
    <RouterLink :to="{ name: 'dashboard' }" class="flex-shrink-0">
      <img
        :src="workspaceStore.current.logo_url || '/v-logo-192x192.png'"
        class="object-contain"
        :class="workspaceStore.current.logo_url ? 'max-h-7 max-w-[140px]' : 'w-6'"
      />
    </RouterLink>
    <button type="button" class="ml-auto flex cursor-pointer items-center" @click="sidebarStore.toggleMobile">
      <VAvatar :image="current_user?.profile_picture" :initials="current_user?.initials" size="md" />
    </button>
  </div>

  <!-- Mobile drawer backdrop -->
  <transition name="fade">
    <div
      v-if="sidebarStore.isMobileOpen"
      class="fixed inset-0 z-40 bg-black/40 sm:hidden"
      @click="sidebarStore.closeMobile"
    />
  </transition>

  <!-- Mobile drawer -->
  <transition name="drawer">
    <aside
      v-if="sidebarStore.isMobileOpen"
      class="fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col border-r border-gray-200 bg-white sm:hidden"
    >
      <!-- Logo + Firm switcher -->
      <div class="flex flex-none flex-col px-5 pb-2 pt-4">
        <RouterLink :to="{ name: 'dashboard' }" class="flex-shrink-0" @click="sidebarStore.closeMobile">
          <img
            :src="workspaceStore.current.logo_url || '/v-logo-192x192.png'"
            class="object-contain hover:opacity-90"
            :class="workspaceStore.current.logo_url ? 'max-h-8 max-w-[160px]' : 'w-6'"
          />
        </RouterLink>
        <div v-if="firms.length > 1" class="mt-4 border-y border-gray-200 py-3">
          <div class="mb-1 px-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Firm</div>
          <ul class="space-y-0.5">
            <li v-for="firm in firms" :key="firm.subdomain">
              <button
                type="button"
                class="flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left text-sm leading-4"
                :class="
                  isFirmActive(firm)
                    ? 'bg-brand/10 font-medium text-brand'
                    : 'text-gray-700 hover:bg-gray-900/5 active:bg-gray-200'
                "
                @click="selectFirm(firm)"
              >
                <span class="truncate">{{ firm.name }}</span>
                <span v-if="isFirmActive(firm)" class="h-2 w-2 flex-shrink-0 rounded-full bg-brand" />
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Viewing-as banner (mobile drawer) -->
      <a
        v-if="authStore.logged_in_as"
        :href="stopViewingAsUrl"
        class="mx-3 mt-3 block rounded-md bg-[#ff853a] px-3 py-2 text-white hover:bg-[#e5762f]"
      >
        <div class="truncate text-xs font-medium">Viewing as {{ current_user?.name }} · read-only</div>
        <div class="mt-1 inline-block rounded bg-white/30 px-2 py-0.5 text-xs font-medium hover:bg-white/50">
          Stop viewing as
        </div>
      </a>

      <!-- Nav items -->
      <nav class="flex-1 overflow-y-auto px-3 pt-5">
        <ul>
          <li v-for="(item, index) in navItems" :key="item?.key ?? `m-divider-${index}`">
            <div v-if="!item" class="my-2 w-full border-t border-gray-200" />

            <!-- Group (e.g. Settings, Accounts): header + sub-items. When `collapsible`,
                 sub-items show only while the group is active; otherwise always visible. -->
            <template v-else-if="item.subItems">
              <VLink
                v-show="!item.hidden"
                :to="item.to"
                class="flex items-center gap-4 whitespace-nowrap rounded-md px-1.5 py-1.5 text-sm font-medium leading-4"
                :class="
                  isGroupActive(item)
                    ? 'text-[#3b88af] hover:bg-[#3b88af]/10 active:bg-[#3b88af]/20'
                    : 'text-gray-700 hover:bg-gray-900/5 active:bg-gray-200'
                "
                @click="sidebarStore.closeMobile"
              >
                <span class="inline-flex flex-shrink-0 items-center justify-center rounded-md bg-transparent p-1.5">
                  <component :is="item.icon" class="h-4 w-4" />
                </span>
                <span>{{ item.label }}</span>
              </VLink>
              <ul
                v-show="!item.hidden && (!item.collapsible || isGroupActive(item))"
                class="mb-1 mt-0.5 space-y-0.5 pl-9"
              >
                <li v-for="subItem in item.subItems" :key="subItem.key" v-show="!subItem.hidden">
                  <VLink
                    :to="subItem.to"
                    class="flex items-center rounded-md px-3 py-1.5 text-xs leading-4"
                    :class="
                      isSubItemActive(subItem)
                        ? 'bg-[#3b88af]/10 font-medium text-[#3b88af]'
                        : 'text-gray-600 hover:bg-gray-900/5 active:bg-gray-200'
                    "
                    @click="sidebarStore.closeMobile"
                  >
                    {{ subItem.label }}
                  </VLink>
                </li>
              </ul>
            </template>

            <component
              v-else
              v-show="!item.hidden"
              :is="typeof item.to === 'string' ? 'a' : 'RouterLink'"
              v-bind="typeof item.to === 'string' ? { href: item.to } : { to: item.to }"
              class="flex cursor-pointer items-center gap-4 whitespace-nowrap rounded-md px-1.5 py-1.5 text-sm font-medium leading-4"
              :class="[
                isNavActive(item)
                  ? 'text-[#3b88af] hover:bg-[#3b88af]/10 active:bg-[#3b88af]/20'
                  : 'text-gray-700 hover:bg-gray-900/5 active:bg-gray-200',
              ]"
              @click="sidebarStore.closeMobile"
            >
              <span class="inline-flex flex-shrink-0 items-center justify-center rounded-md bg-transparent p-1.5">
                <component :is="item.icon" class="h-4 w-4" />
              </span>
              <span>{{ item.label }}</span>
              <span
                v-if="item.badge"
                class="rotate-12 rounded bg-[#3b88af] px-1 py-0.5 text-[9px] font-semibold leading-none text-white"
              >
                {{ item.badge }}
              </span>
            </component>
          </li>
        </ul>
      </nav>

      <!-- Bottom section: profile -->
      <div class="flex-none border-t border-gray-200 px-3 py-3">
        <div class="relative" ref="mobileProfileTarget">
          <div
            class="flex cursor-pointer items-center gap-3 rounded-md px-1.5 py-1.5 hover:bg-gray-900/5"
            @click="isMobileProfileClicked = !isMobileProfileClicked"
          >
            <VAvatar :image="current_user?.profile_picture" :initials="current_user?.initials" size="sm" />
            <div class="min-w-0 flex-1 overflow-hidden">
              <div class="truncate text-sm font-semibold leading-tight">{{ titleCase(current_user?.name) }}</div>
              <div class="truncate text-xs text-gray-500">{{ titleCase(current_user?.role?.replace('_', ' ')) }}</div>
            </div>
          </div>
          <!-- Profile dropdown -->
          <div
            v-if="isMobileProfileClicked"
            class="absolute bottom-full left-0 z-30 mb-1 flex w-full flex-col gap-0.5 rounded-lg border border-gray-300 bg-white py-1 shadow-lg"
          >
            <VLink
              :to="link.to"
              v-for="link in profile_links"
              :key="link.label"
              class="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-[#e8f1f7] hover:text-[#006591]"
              @click="sidebarStore.closeMobile"
            >
              <component :is="link.icon" class="h-4 w-4 text-gray-500" />
              {{ link.label }}
            </VLink>
          </div>
        </div>
      </div>
    </aside>
  </transition>
</template>

<style scoped>
.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.2s ease;
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(-100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
